// src/utils/charts/theme.ts
/**
 * Chart Theme & Configuration Factory
 * 
 * Centralized theme definitions and configuration builders for ECharts.
 * Provides consistent dark theme styling across all charts.
 * 
 * Includes:
 * - Base colors for text, axis, semantic values
 * - Multiple color palettes for different contexts
 * - Card accent color presets
 * - Helper functions for consistent styling
 */

import type { 
  EChartsOption, 
  GridComponentOption, 
  TooltipComponentOption,
  LegendComponentOption,
} from 'echarts';

// ============================================================================
// THEME COLORS
// ============================================================================

export const CHART_COLORS = {
  // Background
  background: 'transparent',
  
  // Text colors
  text: {
    primary: '#f3f4f6',
    secondary: '#cbd5e1',
    muted: '#94a3b8',
    dark: '#64748b',
  },
  
  // Axis colors
  axis: {
    line: '#4b5563',
    label: '#94a3b8',
    splitLine: 'rgba(148, 163, 184, 0.15)',
    splitLineLight: 'rgba(148, 163, 184, 0.2)',
  },
  
  // Semantic colors (standard intensity)
  semantic: {
    positive: '#22c55e',
    negative: '#ef4444',
    neutral: '#94a3b8',
    warning: '#f59e0b',
    info: '#38bdf8',
  },
  
  // Muted semantic colors (for dark UI, softer appearance)
  semanticMuted: {
    positive: '#34d399',  // Softer green
    negative: '#f87171',  // Softer red
  },
  
  // Deep semantic colors (for large areas like treemaps)
  semanticDeep: {
    positive: '#059669',  // Deep emerald
    negative: '#dc2626',  // Deep rose
    neutral: '#374151',   // Dark slate (neutral base)
  },
  
  // Series palette (for multi-line charts) - Dashboard default
  series: [
    '#38bdf8', // Sky blue
    '#a78bfa', // Purple
    '#34d399', // Emerald
    '#fb923c', // Orange
    '#f472b6', // Pink
    '#60a5fa', // Blue
    '#4ade80', // Green
    '#fbbf24', // Amber
    '#c084fc', // Violet
    '#22d3ee', // Cyan
  ],
  
  // Gradient presets
  gradients: {
    blueArea: 'rgba(56,189,248,0.12)',
    greenArea: 'rgba(34,197,94,0.15)',
    redArea: 'rgba(239,68,68,0.15)',
    amberArea: 'rgba(245,158,11,0.15)',
  },
  
  // Tooltip
  tooltip: {
    background: 'rgba(17, 24, 39, 0.95)',
    border: '#374151',
    text: '#f3f4f6',
  },
  
  // Quadrant colors (for RRG-style charts)
  quadrants: {
    leading: 'rgba(34,197,94,0.1)',
    weakening: 'rgba(234,179,8,0.1)',
    lagging: 'rgba(239,68,68,0.1)',
    improving: 'rgba(59,130,246,0.1)',
  },
  
  // Heatmap palettes
  heatmaps: {
    // Diverging (negative to positive)
    diverging: ['#ef4444', '#1f2937', '#22c55e'],
    divergingCorrelation: ['rgba(239,68,68,0.28)', 'rgba(15,23,42,0.12)', 'rgba(34,197,94,0.28)'],
    // Sequential (funding rates, etc.)
    sequential: ['#4b006e', '#2b2f77', '#1e6f8f', '#14a38b', '#5fd067', '#ffd12a', '#ff8a00'],
  },
} as const;

// ============================================================================
// COLOR PALETTES - Multiple palettes for different contexts
// ============================================================================

/**
 * Series color palettes for different contexts.
 * Use `getSeriesPalette()` to retrieve.
 */
export const SERIES_PALETTES = {
  /** Default dashboard palette - bright, varied colors */
  default: [
    '#38bdf8', // Sky blue
    '#a78bfa', // Purple
    '#34d399', // Emerald
    '#fb923c', // Orange
    '#f472b6', // Pink
    '#60a5fa', // Blue
    '#4ade80', // Green
    '#fbbf24', // Amber
    '#c084fc', // Violet
    '#22d3ee', // Cyan
  ],
  
  /** Assistant palette - more saturated, works well on dark cards */
  assistant: [
    '#8b5cf6', // Violet
    '#06b6d4', // Cyan
    '#f59e0b', // Amber
    '#ec4899', // Pink
    '#10b981', // Emerald
    '#3b82f6', // Blue
    '#f97316', // Orange
    '#a855f7', // Purple
    '#14b8a6', // Teal
    '#f43f5e', // Rose
  ],
  
  /** Monochrome palette - for focused single-metric views */
  monochrome: [
    '#8b5cf6', // Primary
    '#a78bfa', // Lighter
    '#7c3aed', // Darker
    '#c4b5fd', // Much lighter
    '#6d28d9', // Much darker
  ],
} as const;

export type SeriesPaletteVariant = keyof typeof SERIES_PALETTES;

/**
 * Get a series color palette by variant name.
 */
export function getSeriesPalette(variant: SeriesPaletteVariant = 'default'): readonly string[] {
  return SERIES_PALETTES[variant];
}

/**
 * Get a color from a palette by index (cycles if index > palette length).
 */
export function getSeriesColor(index: number, variant: SeriesPaletteVariant = 'default'): string {
  const palette = SERIES_PALETTES[variant];
  return palette[index % palette.length] as string;
}

// ============================================================================
// CARD ACCENT PRESETS - For varied chart card styling
// ============================================================================

/**
 * Card accent color presets.
 * Each preset includes primary (hex), hover (hex), and accent (RGB for rgba()).
 */
export const CARD_ACCENTS = [
  { primary: '#8b5cf6', hover: '#a78bfa', accent: '139, 92, 246' },   // Violet
  { primary: '#06b6d4', hover: '#22d3ee', accent: '6, 182, 212' },    // Cyan
  { primary: '#f59e0b', hover: '#fbbf24', accent: '245, 158, 11' },   // Amber
  { primary: '#ec4899', hover: '#f472b6', accent: '236, 72, 153' },   // Pink
  { primary: '#10b981', hover: '#34d399', accent: '16, 185, 129' },   // Emerald
  { primary: '#3b82f6', hover: '#60a5fa', accent: '59, 130, 246' },   // Blue
  { primary: '#f97316', hover: '#fb923c', accent: '249, 115, 22' },   // Orange
  { primary: '#a855f7', hover: '#c084fc', accent: '168, 85, 247' },   // Purple
] as const;

export type CardAccent = typeof CARD_ACCENTS[number];

/**
 * Get a card accent color preset by index or string hash.
 * Useful for assigning consistent colors to charts based on their ID.
 * 
 * @param indexOrId - Numeric index or string to hash
 * @returns CardAccent preset
 * 
 * @example
 * // By index
 * const accent = getCardAccent(0); // Violet
 * 
 * // By string hash (consistent for same string)
 * const accent = getCardAccent('chart_1'); // Some color based on hash
 */
export function getCardAccent(indexOrId: number | string): CardAccent {
  let index: number;
  
  if (typeof indexOrId === 'number') {
    index = indexOrId;
  } else {
    // Hash string to get consistent index
    let hash = 0;
    for (let i = 0; i < indexOrId.length; i++) {
      hash = ((hash << 5) - hash) + indexOrId.charCodeAt(i);
      hash |= 0; // Convert to 32-bit integer
    }
    index = Math.abs(hash);
  }
  
  return CARD_ACCENTS[index % CARD_ACCENTS.length]!;
}

// ============================================================================
// SEMANTIC COLOR HELPERS
// ============================================================================

export type SemanticIntensity = 'standard' | 'muted' | 'deep';

/** Common shape for all semantic color variants */
export interface SemanticColorSet {
  positive: string;
  negative: string;
  neutral: string;
}

/**
 * Get semantic positive/negative/neutral colors by intensity.
 * 
 * @param intensity - 'standard' for normal, 'muted' for dark UI, 'deep' for large areas
 * @returns SemanticColorSet with positive, negative, and neutral colors
 */
export function getSemanticColors(intensity: SemanticIntensity = 'standard'): SemanticColorSet {
  switch (intensity) {
    case 'muted':
      return {
        positive: CHART_COLORS.semanticMuted.positive,
        negative: CHART_COLORS.semanticMuted.negative,
        neutral: CHART_COLORS.semantic.neutral, // Use standard neutral
      };
    case 'deep':
      return {
        positive: CHART_COLORS.semanticDeep.positive,
        negative: CHART_COLORS.semanticDeep.negative,
        neutral: CHART_COLORS.semanticDeep.neutral,
      };
    default:
      return {
        positive: CHART_COLORS.semantic.positive,
        negative: CHART_COLORS.semantic.negative,
        neutral: CHART_COLORS.semantic.neutral,
      };
}
}

/**
 * Get color based on value sign (positive/negative).
 * Useful for bar charts, change indicators, etc.
 * 
 * @param value - Number to check sign
 * @param intensity - Color intensity variant
 */
export function getValueColor(value: number, intensity: SemanticIntensity = 'standard'): string {
  const colors = getSemanticColors(intensity);
  return value >= 0 ? colors.positive : colors.negative;
}

/**
 * Interpolate between two hex colors.
 * Useful for gradient-based color encoding.
 * 
 * @param color1 - Starting color (hex)
 * @param color2 - Ending color (hex)
 * @param factor - Interpolation factor (0 = color1, 1 = color2)
 */
export function interpolateColor(color1: string, color2: string, factor: number): string {
  const hex = (c: string) => parseInt(c, 16);
  const r1 = hex(color1.slice(1, 3)), g1 = hex(color1.slice(3, 5)), b1 = hex(color1.slice(5, 7));
  const r2 = hex(color2.slice(1, 3)), g2 = hex(color2.slice(3, 5)), b2 = hex(color2.slice(5, 7));
  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Get color for a diverging value (maps value to color between negative and positive).
 * 
 * @param value - The value to color
 * @param min - Minimum value in range (maps to negative color)
 * @param max - Maximum value in range (maps to positive color)
 * @param intensity - Color intensity variant
 */
export function getDivergingColor(
  value: number, 
  min: number, 
  max: number, 
  intensity: SemanticIntensity = 'deep'
): string {
  const colors = getSemanticColors(intensity);
  const neutral = CHART_COLORS.semanticDeep.neutral;
  
  if (min >= 0 || max <= 0) {
    // All same sign - just use intensity
    const normalized = max !== min ? (value - min) / (max - min) : 0.5;
    return interpolateColor(colors.negative, colors.positive, normalized);
  }
  
  // Spans zero - interpolate through neutral
  if (value >= 0) {
    const intensity = max > 0 ? Math.min(value / max, 1) : 0;
    return interpolateColor(neutral, colors.positive, 0.4 + intensity * 0.6);
  } else {
    const intensity = min < 0 ? Math.min(Math.abs(value) / Math.abs(min), 1) : 0;
    return interpolateColor(neutral, colors.negative, 0.4 + intensity * 0.6);
  }
}

// ============================================================================
// BASE CONFIGURATIONS
// ============================================================================

/**
 * Base tooltip styling (just the theme, no trigger/formatter)
 * Use this to add theme to existing tooltip configs
 */
export const TOOLTIP_THEME: {
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  textStyle: { color: string };
  padding: number[];
} = {
  backgroundColor: CHART_COLORS.tooltip.background,
  borderColor: CHART_COLORS.tooltip.border,
  borderWidth: 1,
  textStyle: { color: CHART_COLORS.tooltip.text },
  padding: [8, 12],
};

/**
 * Base tooltip configuration
 */
export function createTooltip(options?: Partial<TooltipComponentOption>): TooltipComponentOption {
  return {
    trigger: 'axis',
    ...TOOLTIP_THEME,
    ...options,
  };
}

/**
 * Create tooltip with custom formatter while keeping theme
 */
export function createThemedTooltip(
  options: Partial<TooltipComponentOption> & { formatter?: unknown }
): TooltipComponentOption {
  return {
    ...TOOLTIP_THEME,
    ...options,
  } as TooltipComponentOption;
}

/**
 * Base grid configuration
 */
export function createGrid(options?: Partial<GridComponentOption>): GridComponentOption {
  return {
    left: 56,
    right: 40,
    top: 28,
    bottom: 28,
    containLabel: false,
    ...options,
  };
}

/**
 * Base legend configuration
 */
export function createLegend(options?: Partial<LegendComponentOption>): LegendComponentOption {
  return {
    show: true,
    textStyle: { color: CHART_COLORS.text.secondary },
    top: 0,
    icon: 'rect',
    itemWidth: 18,
    itemHeight: 3,
    ...options,
  };
}

// ============================================================================
// AXIS CONFIGURATIONS
// ============================================================================

export interface XAxisConfig {
  type?: 'category' | 'time' | 'value';
  data?: (string | number)[];
  name?: string;
  boundaryGap?: boolean;
  rotate?: number;
  showMaxLabel?: boolean;
}

/**
 * Create X axis configuration
 * Note: Returns untyped object due to ECharts strict types - cast in context
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createXAxis(config: XAxisConfig = {}): any {
  const { type = 'category', data, name, boundaryGap = false, rotate = 0, showMaxLabel = true } = config;
  
  return {
    type,
    ...(data ? { data } : {}),
    ...(name ? { name } : {}),
    boundaryGap,
    axisLabel: {
      color: CHART_COLORS.axis.label,
      margin: 14,
      showMaxLabel,
      rotate,
    },
    axisTick: { show: false },
    axisLine: { show: false },
    nameTextStyle: { color: CHART_COLORS.text.secondary, fontSize: 10 },
  };
}

export interface YAxisConfig {
  type?: 'value' | 'log';
  name?: string;
  position?: 'left' | 'right';
  formatter?: (value: number) => string;
  min?: number | 'auto';
  max?: number | 'auto';
  splitNumber?: number;
  inverse?: boolean;
}

/**
 * Create Y axis configuration
 * Note: Returns untyped object due to ECharts strict types - cast in context
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createYAxis(config: YAxisConfig = {}): any {
  const { type = 'value', name, position = 'left', formatter, min, max, splitNumber, inverse } = config;
  
  // When min/max are explicitly set, disable scale to respect the bounds
  // Otherwise enable scale: true so axis range fits the data (doesn't force 0)
  const hasManualBounds = min !== undefined && min !== 'auto' && max !== undefined && max !== 'auto';
  
  return {
    type,
    ...(name ? { name } : {}),
    position,
    ...(min !== undefined && min !== 'auto' ? { min } : {}),
    ...(max !== undefined && max !== 'auto' ? { max } : {}),
    scale: !hasManualBounds, // Enable scale to auto-fit data range when no manual bounds
    ...(splitNumber ? { splitNumber } : {}),
    ...(inverse ? { inverse } : {}),
    axisLabel: {
      color: CHART_COLORS.axis.label,
      ...(formatter ? { formatter } : {}),
    },
    splitLine: {
      show: true,
      lineStyle: { color: CHART_COLORS.axis.splitLine },
    },
    axisLine: { show: false },
    nameTextStyle: { color: CHART_COLORS.text.secondary, fontSize: 10 },
  };
}

// ============================================================================
// VISUAL MAP CONFIGURATIONS
// ============================================================================

export interface VisualMapConfig {
  type: 'diverging' | 'sequential' | 'correlation';
  min?: number;
  max?: number;
  show?: boolean;
}

/**
 * Create visual map configuration for heatmaps
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createVisualMap(config: VisualMapConfig): any {
  const { type, min = -1, max = 1, show = true } = config;
  
  const colors = type === 'sequential' 
    ? [...CHART_COLORS.heatmaps.sequential]
    : type === 'correlation'
    ? [...CHART_COLORS.heatmaps.divergingCorrelation]
    : [...CHART_COLORS.heatmaps.diverging];
  
  return {
    min,
    max,
    calculable: type !== 'sequential',
    show,
    orient: 'horizontal',
    left: 'center',
    bottom: 0,
    textStyle: { color: CHART_COLORS.text.secondary },
    inRange: { color: colors },
  };
}

// ============================================================================
// SERIES CONFIGURATIONS
// ============================================================================

export interface LineSeriesConfig {
  name: string;
  data: (number | null)[];
  color?: string;
  width?: number;
  smooth?: boolean;
  showSymbol?: boolean;
  areaStyle?: boolean;
  dashed?: boolean;
}

/**
 * Create line series configuration
 */
export function createLineSeries(config: LineSeriesConfig, index: number = 0) {
  const { 
    name, 
    data, 
    color = CHART_COLORS.series[index % CHART_COLORS.series.length],
    width = 2,
    smooth = false,
    showSymbol = false,
    areaStyle = false,
    dashed = false,
  } = config;
  
  return {
    name,
    type: 'line' as const,
    data,
    smooth,
    showSymbol,
    lineStyle: { 
      width, 
      color,
      type: dashed ? 'dashed' as const : 'solid' as const,
    },
    itemStyle: { color },
    ...(areaStyle ? { 
      areaStyle: { 
        color: color?.replace(')', ', 0.12)').replace('rgb', 'rgba') || CHART_COLORS.gradients.blueArea 
      } 
    } : {}),
  };
}

export interface BarSeriesConfig {
  name?: string;
  data: (number | null)[];
  color?: string | ((params: { value: number }) => string);
  barWidth?: number | string;
  horizontal?: boolean;
}

/**
 * Create bar series configuration
 */
export function createBarSeries(config: BarSeriesConfig, index: number = 0) {
  const { 
    name, 
    data, 
    color = CHART_COLORS.series[index % CHART_COLORS.series.length],
    barWidth = 10,
    horizontal = false,
  } = config;
  
  return {
    name,
    type: 'bar' as const,
    data,
    barWidth,
    itemStyle: { 
      color,
      borderRadius: horizontal ? [0, 2, 2, 0] : [2, 2, 0, 0],
    },
  };
}

export interface ScatterSeriesConfig {
  name?: string;
  data: Array<[number, number, ...unknown[]]>;
  symbolSize?: number | ((value: unknown[]) => number);
  color?: string | ((params: { data: unknown[] }) => string);
}

/**
 * Create scatter series configuration
 */
export function createScatterSeries(config: ScatterSeriesConfig, index: number = 0) {
  const { 
    name, 
    data, 
    symbolSize = 12,
    color = CHART_COLORS.series[index % CHART_COLORS.series.length],
  } = config;
  
  return {
    name,
    type: 'scatter' as const,
    data,
    symbolSize,
    itemStyle: { 
      color,
      opacity: 0.85,
    },
    emphasis: { 
      scale: 1.5,
      focus: 'series' as const,
    },
  };
}

export interface HeatmapSeriesConfig {
  name?: string;
  data: Array<[number, number, number]>;
  showLabel?: boolean;
  labelFormatter?: (params: { value: [number, number, number] }) => string;
}

/**
 * Create heatmap series configuration
 */
export function createHeatmapSeries(config: HeatmapSeriesConfig) {
  const { name = 'Heatmap', data, showLabel = true, labelFormatter } = config;
  
  return {
    name,
    type: 'heatmap' as const,
    data,
    label: {
      show: showLabel,
      color: CHART_COLORS.text.primary,
      fontSize: 11,
      formatter: labelFormatter,
    },
    itemStyle: {
      borderColor: 'rgba(148,163,184,0.18)',
      borderWidth: 1,
    },
  };
}

// ============================================================================
// COMPLETE CHART OPTION BUILDERS
// ============================================================================

export interface TimeSeriesChartConfig {
  dates: (string | number)[];
  series: LineSeriesConfig[];
  yAxisFormatter?: (value: number) => string;
  yAxisName?: string;
  showLegend?: boolean;
  showMeanBands?: boolean;
}

/**
 * Build complete time series chart option
 */
export function buildTimeSeriesOption(config: TimeSeriesChartConfig): EChartsOption {
  const { dates, series, yAxisFormatter, yAxisName, showLegend = true } = config;
  
  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: createTooltip(),
    legend: showLegend ? createLegend() : { show: false },
    grid: createGrid(),
    xAxis: createXAxis({ 
      data: dates,
      boundaryGap: false,
    }),
    yAxis: createYAxis({ 
      ...(yAxisFormatter ? { formatter: yAxisFormatter } : {}),
      ...(yAxisName ? { name: yAxisName } : {}),
    }),
    series: series.map((s, i) => createLineSeries(s, i)),
  } as EChartsOption;
}

export interface BarChartConfig {
  categories: string[];
  series: BarSeriesConfig[];
  horizontal?: boolean;
  yAxisFormatter?: (value: number) => string;
}

/**
 * Build complete bar chart option
 */
export function buildBarChartOption(config: BarChartConfig): EChartsOption {
  const { categories, series, horizontal = false, yAxisFormatter } = config;
  
  const xAxisConfig = horizontal 
    ? createYAxis(yAxisFormatter ? { formatter: yAxisFormatter } : {})
    : createXAxis({ data: categories, rotate: categories.length > 10 ? 20 : 0 });
  
  const yAxisConfig = horizontal
    ? createXAxis({ data: categories })
    : createYAxis(yAxisFormatter ? { formatter: yAxisFormatter } : {});
  
  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: createTooltip({ trigger: 'axis', axisPointer: { type: 'shadow' } }),
    grid: createGrid({ left: horizontal ? 120 : 60 }),
    xAxis: xAxisConfig,
    yAxis: yAxisConfig,
    series: series.map((s, i) => createBarSeries({ ...s, horizontal }, i)),
  } as EChartsOption;
}

export interface HeatmapChartConfig {
  xLabels: string[];
  yLabels: string[];
  data: Array<[number, number, number]>;
  visualMapType?: 'diverging' | 'sequential' | 'correlation';
  visualMapRange?: [number, number];
  showLabel?: boolean;
  labelFormatter?: (params: { value: [number, number, number] }) => string;
}

/**
 * Build complete heatmap chart option
 */
export function buildHeatmapOption(config: HeatmapChartConfig): EChartsOption {
  const { 
    xLabels, 
    yLabels, 
    data, 
    visualMapType = 'diverging',
    visualMapRange = [-1, 1],
    showLabel = true,
    labelFormatter,
  } = config;
  
  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: {
      position: 'top',
    },
    grid: createGrid({ left: 100, bottom: 72 }),
    xAxis: createXAxis({ 
      data: xLabels, 
      rotate: xLabels.length > 8 ? 30 : 0,
    }),
    yAxis: {
      type: 'category',
      data: yLabels,
      axisLabel: { color: CHART_COLORS.axis.label, fontSize: 11 },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    visualMap: createVisualMap({ 
      type: visualMapType, 
      min: visualMapRange[0], 
      max: visualMapRange[1],
    }),
    series: [createHeatmapSeries({ 
      data, 
      showLabel, 
      ...(labelFormatter ? { labelFormatter } : {}),
    })],
  } as EChartsOption;
}

export interface ScatterChartConfig {
  data: Array<[number, number, ...unknown[]]>;
  xAxisName?: string;
  yAxisName?: string;
  xAxisFormatter?: (value: number) => string;
  yAxisFormatter?: (value: number) => string;
  symbolSize?: number | ((value: unknown[]) => number);
  colorFn?: (params: { data: unknown[] }) => string;
}

/**
 * Build complete scatter chart option
 */
export function buildScatterOption(config: ScatterChartConfig): EChartsOption {
  const { 
    data, 
    xAxisName, 
    yAxisName, 
    xAxisFormatter, 
    yAxisFormatter,
    symbolSize = 12,
    colorFn,
  } = config;
  
  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: { trigger: 'item' },
    grid: createGrid({ containLabel: true }),
    xAxis: createYAxis({ 
      ...(xAxisName ? { name: xAxisName } : {}),
      ...(xAxisFormatter ? { formatter: xAxisFormatter } : {}),
    }),
    yAxis: createYAxis({ 
      ...(yAxisName ? { name: yAxisName } : {}),
      ...(yAxisFormatter ? { formatter: yAxisFormatter } : {}),
    }),
    series: [createScatterSeries({ 
      data, 
      symbolSize,
      ...(colorFn ? { color: colorFn } : {}),
    })],
  } as EChartsOption;
}

// ============================================================================
// TOOLTIP POSITION HELPER
// ============================================================================

/**
 * Smart tooltip positioning to avoid cutoff at edges
 */
export function smartTooltipPosition(
  point: number[],
  _params: unknown,
  _dom: unknown,
  _rect: unknown,
  size: { contentSize: number[]; viewSize: number[] }
): [number, number] {
  const x = point[0] ?? 0;
  const y = point[1] ?? 0;
  const contentWidth = size.contentSize[0] ?? 0;
  const contentHeight = size.contentSize[1] ?? 0;
  const viewWidth = size.viewSize[0] ?? 0;
  
  let posX = x - contentWidth / 2;
  let posY = y - contentHeight - 10;
  
  if (posY < 0) posY = y + 10;
  if (posX < 0) posX = 5;
  if (posX + contentWidth > viewWidth) posX = viewWidth - contentWidth - 5;
  
  return [posX, posY];
}

